import { DragDropModule } from '@angular/cdk/drag-drop';
import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  runInInjectionContext,
  signal,
  ViewChild,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { PointExtensions } from '@foblex/2d';
import {
  EFMarkerType,
  FCanvasComponent,
  FCreateConnectionEvent,
  FCreateNodeEvent,
  FFlowModule,
  FReassignConnectionEvent,
  FSelectionChangeEvent,
} from '@foblex/flow';
import { SiaActionFired, SiaActionsService } from '@sia/actions';
import {
  GoogleAuthService,
  SiaGoogleAuthButtonComponent,
} from '@sia/google-auth';
import { SiaGridWithActionsComponent } from '@sia/grid';
import {
  SelectBoxMultiComponent,
  SiaButtonComponent,
  SiaCheckBoxComponent,
  SiaDateTimeBoxComponent,
  SiaSelectBoxComponent,
  SiaTextBoxComponent,
  SiaTimeBoxComponent,
} from '@sia/inputs';

import {
  FileService,
  notify,
  PromptDialogComponent,
  retriveCurrentRouteData,
  SiaAccordionComponent,
  SiaBaseActionService,
  SiaColorContrastAutoDirective,
  SiaComponentConfigPerspectives,
  SiaComponentConfigService,
  SiaInitDirective,
  SiaMatContextMenuDirective,
  SiaNewRouterService,
  SiaParamsAndPatternsService,
  SiaRouteData,
  SiaSessionService,
  SiaSignalrService,
  SiaTranslatePipe,
  SiaUsersService,
} from '@sia/shared';
import { getHours, getMinutes } from 'date-fns';
import { Guid } from 'guid-typescript';
import { firstValueFrom } from 'rxjs';
import { WorkflowActionConfigurationComponent } from '../../dialogs/workflow-action-configuration/workflow-action-configuration.component';
import { WorkflowUploadFileComponent } from '../../dialogs/workflow-upload-files/workflow-upload-files.component';
import { AIProcessActionDTO } from '../../interfaces/workflow-action';
import { WorkFlowConfiguration } from '../../interfaces/workflow-configuration';

@Component({
  selector: 'sia-crud-configurator',
  templateUrl: './sia-workflow-configurator.component.html',
  styleUrls: ['./sia-workflow-configurator.component.scss'],
  standalone: true,
  imports: [
    SiaTranslatePipe,
    DragDropModule,
    MatTabGroup,
    MatTab,
    FFlowModule,
    MatIconModule,
    SiaTextBoxComponent,
    SiaSelectBoxComponent,
    SiaColorContrastAutoDirective,
    MatMenuModule,
    SiaMatContextMenuDirective,
    SiaInitDirective,
    SiaAccordionComponent,
    SiaCheckBoxComponent,
    SiaButtonComponent,
    SelectBoxMultiComponent,
    SiaGridWithActionsComponent,
    MatProgressSpinnerModule,
    SiaTimeBoxComponent,
    MatIconButton,
    SiaGoogleAuthButtonComponent,
    SiaDateTimeBoxComponent,
  ],
})
export class SiaWorkflowConfiguratorComponent {
  @ViewChild(FCanvasComponent, { static: false })
  private fCanvas?: FCanvasComponent;
  @ViewChild(MatMenuTrigger) matMenuTrigger!: MatMenuTrigger;

  readonly eMarkerType = EFMarkerType;

  contextMenuNode: any = null;

  hovering = signal<any>({});

  #siaSignalrService = inject(SiaSignalrService);
  #fileService = inject(FileService);
  #matDialog = inject(MatDialog);
  #siaSessionService = inject(SiaSessionService);
  #userService = inject(SiaUsersService);
  #baseActionService = inject(SiaBaseActionService);
  #paramsAndPatternsService = inject(SiaParamsAndPatternsService);
  #googleAuthService = inject(GoogleAuthService);

  #cdr = inject(ChangeDetectorRef);
  pageData?: SiaRouteData;
  #siaComponentConfigService = inject(SiaComponentConfigService);
  #siaActionsService = inject(SiaActionsService);
  #routerService = inject(SiaNewRouterService);
  #injector = inject(Injector);
  #destroyRef = inject(DestroyRef);
  client_id: string = '';

  workFlowConfiguration: WorkFlowConfiguration = {
    actions: [],
    connections: [],
  };
  #perspective?: SiaComponentConfigPerspectives | null;
  selectedTabIndex = 0;

  isAdmin: boolean = false;
  // Variabili inizilai in base all'hook del workflow
  startingVariables: { [key: string]: string[] } = {
    FolderWatcher: ['Path', 'Size', 'FileSha256', 'FileName'],
    FolderTemporized: ['Path', 'Size', 'FileSha256', 'FileName'],
    HttpRequest: ['Path', 'Size', 'FileSha256', 'FileName'],
    OutlookEmailWatcher: ['Path', 'FileSha256', 'FileName', 'NumberAttachments', 'Date', 'From', 'Subject', 'Cc', 'Bcc', 'Body', 'Account'],

  };

  // Variabili di base in base all'azione (se si aggiunge una azione, si ereditano anche altre variabili)
  // Il nome chiave è lo stesso del tipo di interfaccia
  startingVariablesFromAction: {
    [key: string]: { variables: string[]; present: boolean };
  } = {
      OCR: { variables: ['_ocr_start_date', '_ocr_end_date'], present: false },
      Chunking: {
        variables: ['_chunking_start_date', '_chunking_end_date'],
        present: false,
      },
    };

  timeInterval: any;
  triggerTypes = [
    { id: 'FolderWatcher', description: 'Controllore di cartella' },
    {
      id: 'FolderTemporized',
      description: 'Controllore di cartella temporizzato',
    },
    { id: 'HttpRequest', description: 'Richieste HTTP' },
    { id: 'SqlTemporized', description: 'Controllore di tabelle temporizzato' },
    { id: 'SqlWatcher', description: 'Controllore di tabelle' },
    {
      id: 'EmailWatcher',
      description: 'Controller GMAIL di posta elettronica',
    },
    {
      id: 'OutlookEmailWatcher',
      description: 'Controller Outlook di posta elettronica',
    },
  ];

  httpUploadType = [{ id: 'file', description: 'File' }];
  isPostgres: boolean = false;
  databases = [
    { id: 'coreDb', description: 'CoreDB', isPostgres: false },
    { id: 'ai', description: 'ai', isPostgres: true },
  ];
  fileTypes = [
    { id: 'jpg', description: 'jpg', selected: false },
    { id: 'png', description: 'png', selected: false },
    { id: 'pdf', description: 'pdf', selected: false },
    { id: 'docx', description: 'docx', selected: false },
    { id: 'xlsx', description: 'xlsx', selected: false },
    { id: 'txt', description: 'txt', selected: false },
    { id: 'html', description: 'html', selected: false },
    { id: 'pptx', description: 'pptx', selected: false },
    { id: 'csv', description: 'csv', selected: false },
  ];

  legenda: any[] = [
    {
      icon: 'Move',
      color: '',
      description: "Abilitata condizione sull'esecuzione dell'azione",
    },
    {
      icon: 'Move',
      color: '',
      description: 'Abilitata condizione sui nodi genitori',
    },
  ];

  variables = signal<
    { id: string; parentId: string; description: string; isDefault: boolean }[]
  >([]);

  actionsBlocks = signal<
    {
      type: string;
      typeDescription: string;
      description: string;
      icon: string;
      color: string;
    }[]
  >([
    {
      type: 'SQL',
      typeDescription: 'SQL',
      description: 'Ottieni o modifica dati su tabelle SQL',
      icon: 'table',
      color: '#336791',
    },
    {
      type: 'FileManager',
      typeDescription: 'Gestione File',
      description: 'Modifica, rinomina, sposta file su un file system',
      icon: 'file_copy',
      color: '#FFA500',
    },
    {
      type: 'ChromaDB',
      typeDescription: 'ChromaDB',
      description: 'Gestisci record su database vettoriali ChromaDB',
      icon: 'database_search',
      color: '#8B5CF6',
    },
    {
      type: 'Chunking',
      typeDescription: 'Chunking',
      description: 'Gestisci il chunking dei dati su database vettoriali',
      icon: 'docs',
      color: '#10B981',
    },
    {
      type: 'OCR',
      typeDescription: 'OCR',
      description: 'Gestisci il riconoscimento ottico dei caratteri (OCR)',
      icon: 'document_scanner',
      color: '#ea6767',
    },

    {
      type: 'Classification',
      typeDescription: 'Classificazione',
      description: 'Classifica documenti o dati in categorie e sottocategorie',
      icon: 'category',
      color: '#F59E0B',
    },
    {
      type: 'Extraction',
      typeDescription: 'Estrazione dati',
      description: 'Estrai informazioni specifiche da documenti o dati',
      icon: 'content_paste_search',
      color: '#3B82F6',
    },
    {
      type: 'ForLoop',
      typeDescription: 'Ciclo For',
      description: 'Esegui un ciclo For su una lista di elementi',
      icon: 'loop',
      color: '#9CA3AF',
    },
    {
      type: 'Chat',
      typeDescription: 'Chat',
      description: 'Apri un chat',
      icon: 'chat',
      color: '#ABAF9C',
    },
    {
      type: 'Trigger',
      typeDescription: 'Trigger',
      description: 'Esegui un workflow',
      icon: 'flowchart',
      color: '#9cafa5',
    },
  ]);

  actionsBlocksObj = computed<{
    [key: string]: {
      type: 'Extraction';
      typeDescription: 'Estrazione dati';
      description: 'Estrai informazioni specifiche da documenti o dati';
      icon: 'content_paste_search';
      color: '#3B82F6';
    };
  }>(() => {
    return this.actionsBlocks().reduce((acc: any, action) => {
      acc[action.type] = action;
      return acc;
    }, {});
  });

  selectedAction: AIProcessActionDTO | null = null;
  #fitToScreenTimeout: any;
  hasDragged = false;
  #executionId: string = '';
  actionsInExecution = signal<{ [key: string]: string }>({});

  // --- WORKFLOW EXECUTION LIST
  workflowInExecution = signal<
    { executionId: string; description: string; status: string }[]
  >([
    // { executionId: 'b', description: 'test.pdf', status: 'complete' },
    // { executionId: 'c', description: 'test.pdf', status: 'complete' },
    // { executionId: 'a', description: 'myFile.pdf', status: 'pending' },
    // { executionId: 'd', description: 'test.pdf', status: 'executing' },
  ]);
  // ---

  constructor() {
    let s1 = this.#siaActionsService
      .actionFired$()
      .subscribe((firedAction: SiaActionFired) => {
        if (
          firedAction?.guid == this.pageData?.guid &&
          firedAction?.componentId == this.pageData?.componentId
        ) {
          this.manageActionFired(firedAction);
        }
      });
    this.#destroyRef.onDestroy(() => {
      s1.unsubscribe();
    });
  }

  // ----- GESTIONE DELLE CONNESSIONI PER INTERFACCIA GRAFICA
  onCreateConnection(event: FCreateConnectionEvent): void {
    if (!event.fInputId) {
      return;
    }
    this.workFlowConfiguration.connections!.push({
      from: event.fOutputId,
      to: event.fInputId,
      description: '',
    });
  }

  onDeleteConnections(): void {
    this.workFlowConfiguration.connections = [];
    this.#cdr.detectChanges();
  }

  onConnectionDropped(event: FReassignConnectionEvent): void {
    if (!event.newTargetId) {
      this.#removeConnection(event);
    } else {
      this.#reassignConnection(event);
    }
    this.#cdr.detectChanges();
  }
  hoveringNode(actionId: string, hovered: boolean) {
    if (!this.isAdmin) {
      return;
    }
    this.hovering.update((prev) => ({ ...prev, [actionId]: hovered }));
  }
  #removeConnection(event: FReassignConnectionEvent): void {
    const connectionIndex = this.#findConnectionIndex(
      event.oldSourceId,
      event.oldTargetId,
    );
    if (connectionIndex === -1) {
      throw new Error('Connection not found');
    }
    this.workFlowConfiguration.connections!.splice(connectionIndex, 1);
  }

  #findConnectionIndex(outputId: string, inputId: string): number {
    return this.workFlowConfiguration.connections!.findIndex(
      (x) => x.from === outputId && x.to === inputId,
    );
  }

  #reassignConnection(event: FReassignConnectionEvent): void {
    this.#removeConnection(event);
    this.workFlowConfiguration.connections!.push({
      from: event.oldSourceId,
      to: event.newTargetId!,
      description: '',
    });
  }
  onSelectionChange($event: FSelectionChangeEvent) {
    if (
      $event.fConnectionIds.length == 0 &&
      $event.fGroupIds.length == 0 &&
      $event.fNodeIds.length == 0
    ) {
      this.selectedAction = null;
      this.#cdr.detectChanges();
      this.selectedTabIndex = 0;
      return;
    }
  }
  async onActionClick(event: any): Promise<void> {
    if (!this.isAdmin) {
      return;
    }
    if (this.hasDragged) {
      this.hasDragged = false;
      return; // Questo perche quando dragghi un nodo viene lanciato anche il click
    }

    let actionIndex = this.workFlowConfiguration.actions.findIndex(
      (x) => x.id === event.id,
    );
    if (actionIndex == -1) {
      return;
    }
    this.selectedAction = this.workFlowConfiguration.actions[actionIndex];
    let dialogRef = this.#matDialog.open(WorkflowActionConfigurationComponent, {
      width: '90vw',
      minWidth: '90vw',
      data: {
        selectedAction: this.selectedAction,
        variables: this.variables(),
      },
    });
    let prevOutput = this.selectedAction.output;
    let prevDesc = this.selectedAction.description;
    let action = await firstValueFrom(dialogRef.afterClosed());
    if (action) {
      this.workFlowConfiguration.actions[actionIndex] = action;
      if (prevOutput != action.output || prevDesc != action.description) {
        this.addVariable(action);
      }
    }
    this.#cdr.detectChanges();
    this.selectedTabIndex = 1;
  }
  // ----

  deleteAction(id: string) {
    if (this.selectedAction && this.selectedAction.id == id) {
      this.selectedAction = null;
      this.selectedTabIndex = 0;
    }

    if (this.workFlowConfiguration.connections) {
      this.workFlowConfiguration.connections =
        this.workFlowConfiguration.connections?.filter(
          (x) => !x.from.includes(id) && !x.to.includes(id),
        );
    }

    this.workFlowConfiguration.actions =
      this.workFlowConfiguration.actions.filter((x) => x.id != id);

    this.#cdr.detectChanges();
  }

  onNodeAdded(event: FCreateNodeEvent) {
    // aggiungi alla configurazione del workflow
    this.workFlowConfiguration.actions.push({
      id: Guid.create().toString(),
      type: event.data, // o qualsiasi tipo predefinito
      position: { x: event.rect.x, y: event.rect.y },
      children: [],
    });
    this.addDefaultActionVariables(event.data);
  }

  addDefaultActionVariables(actionType: string) {
    if (
      !this.startingVariablesFromAction[actionType] ||
      this.startingVariablesFromAction[actionType].present == true
    ) {
      return;
    }
    this.startingVariablesFromAction[actionType].present = true;
    for (let variable of this.startingVariablesFromAction[actionType]
      .variables || []) {
      this.variables.update((prev) => [
        ...prev,
        {
          id: variable,
          parentId: '',
          description: variable + ` - Variabile default per ${actionType}`,
          isDefault: true,
        },
      ]);
    }
  }

  async manageActionFired(firedAction: SiaActionFired) {
    switch (firedAction.action?.id) {
      case 'navigate':
        break;
      case 'save':
        this.saveConfiguration();
        break;
      case 'back':
        // this.#siaSessionService.clear('change-config');
        // this.#siaSessionService.clear('form-crud-warnings');
        // this.#siaSessionService.clear('form-crud-id');
        break;

      case 'upload-file':
        let dialogRef = this.#matDialog.open(WorkflowUploadFileComponent, {
          width: '90vw',
          minWidth: '90vw',
          data: { id: this.workFlowConfiguration.id },
        });
        let action = await firstValueFrom(dialogRef.afterClosed());
        break;
      case 'download-perspective':
        this.#fileService.downloadPerspective(
          this.workFlowConfiguration.id || '',
          'workflow',
        );
        break;
      default:
        break;
    }
  }

  manageTriggerActions(
    type?:
      | 'FolderWatcher'
      | 'FolderTemporized'
      | 'HttpRequest'
      | 'SqlTemporized'
      | 'SqlWatcher'
      | 'EmailWatcher'
      | 'OutlookEmailWatcher',
    index: number = -1,
  ) {
    if (!this.workFlowConfiguration.triggers) {
      return;
    }

    if (type == this.workFlowConfiguration.triggers[index]?.type) {
      return;
    }
    var triggerFileFound = this.workFlowConfiguration.triggers.findIndex(
      (trigger) =>
        trigger.type == 'HttpRequest' && trigger.uploadType == 'file',
    );

    if (triggerFileFound == -1) {
      this.#baseActionService.handleAction(
        this.pageData?.componentId ?? '',
        this.pageData?.guid ?? '',
        'upload-file',
        false,
      );
    }

    if (index >= 0 && type) {
      switch (type) {
        case 'FolderWatcher':
          this.workFlowConfiguration.triggers.splice(index, 1, {
            type: 'FolderWatcher',
            folderPath: '',
            includeSubfolders: true,
            fileTypes: [],
          });

          break;
        case 'FolderTemporized':
          this.workFlowConfiguration.triggers.splice(index, 1, {
            type: 'FolderTemporized',
            folderPath: '',
            includeSubfolders: true,
            fileTypes: [],
          });
          break;
        case 'HttpRequest':
          this.workFlowConfiguration.triggers.splice(index, 1, {
            type: 'HttpRequest',
            uploadFilePath: '',
            uploadType: '',
          });
          break;
        case 'SqlTemporized':
          this.workFlowConfiguration.triggers.splice(index, 1, {
            type: 'SqlTemporized',
            viewName: '',
            viewSchema: '',
            connectionStringName: '',
          });
          break;
        case 'SqlWatcher':
          this.workFlowConfiguration.triggers.splice(index, 1, {
            type: 'SqlWatcher',
            viewName: '',
            viewSchema: '',
            connectionStringName: '',
          });
          break;
        case 'EmailWatcher':
          this.workFlowConfiguration.triggers.splice(index, 1, {
            type: 'EmailWatcher',
            account: '',
            folderName: '',
            filePath: '',
          });
          break;
        case 'OutlookEmailWatcher':
          this.workFlowConfiguration.triggers.splice(index, 1, {
            type: 'OutlookEmailWatcher',
            accounts: [],
            folderName: '',
            filePath: '',
          });
          break;
      }
    }
  }

  addAccount(trigger: any) {
    if (!trigger.accounts) {
      trigger.accounts = [];
    }
    trigger.accounts.push('');
  }

  addDomain(trigger: any) {
    if (!trigger.excludeDomain) {
      trigger.excludeDomain = [];
    }
    trigger.excludeDomain.push('');
  }

  removeAccount(trigger: any, index: number) {
    trigger.accounts.splice(index, 1);
  }

  removeDomain(trigger: any, index: number) {
    trigger.excludeDomain.splice(index, 1);
  }

  async saveConfiguration() {
    this.workFlowConfiguration.triggers?.forEach((trigger: any) => {
      trigger.startTimes = [];
      trigger?.configurationStartTime?.forEach((time: Date) => {
        trigger.startTimes.push({
          hour: getHours(time),
          minute: getMinutes(time),
          second: 0,
        });
      });
    });
    this.workFlowConfiguration.actions.map((x) => (x.children = []));
    this.workFlowConfiguration.connections?.forEach((connection) => {
      let toWithoutLeadingDirection = connection.to.replace(/-.$/, '');
      let fromWithoutLeadingDirection = connection.from.replace(/-.$/, '');
      this.workFlowConfiguration.actions
        .find((x) => x.id == fromWithoutLeadingDirection)
        ?.children?.push(toWithoutLeadingDirection);
    });
    this.workFlowConfiguration.connections =
      this.workFlowConfiguration.connections;
    this.workFlowConfiguration.actions =
      this.workFlowConfiguration.actions?.map((x) => {
        const { type, ...rest } = x;
        return { type, ...rest } as AIProcessActionDTO;
      });

    // this.workFlowConfiguration.triggers =
    //   this.workFlowConfiguration.triggers?.map((x) => {
    //     const { type, ...rest } = x;
    //     return { type, ...rest };
    //   });

    await this.updatePerspective();
    notify('Configurazione salvata con successo', 2000, 'success');
  }

  async ngOnInit(): Promise<void> {
    try {
      this.client_id = await this.#googleAuthService.getClientId('gmail');
    } catch (e) {
      this.client_id = '';
    }

    this.isAdmin =
      this.#userService.userHaveGroup('admin') ||
      this.#userService.getRealUserId()! < 0;

    let s0 = this.#routerService.routeChanged$.subscribe(async () => {
      var pageData: any = null;
      runInInjectionContext(this.#injector, () => {
        pageData = retriveCurrentRouteData() ?? {};
      });
      if (!pageData?.guid || pageData?.guid == this.pageData?.guid) {
        return;
      }
      this.pageData = pageData!;
      this.pageData = pageData;
      this.#perspective =
        await this.#siaComponentConfigService.getDefaultPerspective(
          'workflow',
          this.pageData!.componentId!, //this.#pageData?.componentId ?? '',
          'MASTER',
        );

      if (!this.#perspective) {
        this.#perspective = {
          componentId: this.pageData!.componentId!, //this.#pageData?.componentId ?? '',
          description: 'DEFAULT',
          configType: 'workflow',
          defaultMode: 'A',
          jsonConfig: JSON.stringify(this.workFlowConfiguration),
        };
        this.#siaComponentConfigService.setComponentPerspective(
          this.#perspective,
        );
      }
      this.workFlowConfiguration = JSON.parse(this.#perspective.jsonConfig!);

      // Migration for OutlookEmailWatcher: account -> accounts
      this.workFlowConfiguration.triggers?.forEach((trigger: any) => {
        if (trigger.type === 'OutlookEmailWatcher') {
          if (
            trigger.account &&
            (!trigger.accounts || trigger.accounts.length === 0)
          ) {
            trigger.accounts = [trigger.account];
          }
          if (!trigger.accounts) {
            trigger.accounts = [];
          }
          if (trigger.account) {
            delete trigger.account;
          }
          if (!trigger.startDate) {
            trigger.startDate = null;
          }
        }
      });
      this.workFlowConfiguration.actions
        .filter((action) => !!action.output)
        .forEach((action) => {
          this.addVariable(action);
        });
      if (!this.workFlowConfiguration.triggers) {
        this.workFlowConfiguration.triggers = [];
      }

      this.manageTriggerActions();
      this.#siaSessionService.updateSessionParams(this.pageData?.guid ?? '', [
        { id: 'workflow-id', value: this.workFlowConfiguration.id ?? '' },
      ]);
      this.#paramsAndPatternsService.notifyHardParamsUpdatedWithGuid(
        this.pageData?.guid ?? '',
      );
      // Script per popolare le posizioni dei blocchi se non sono gia stati popolati (in caso di configurazioni scritte a mano)
      // this.populatePositions(this.workFlowConfiguration.actions);

      for (let action of this.workFlowConfiguration.actions) {
        // ---- SOLO PER PROVA
        if (!action.position) {
          action.position = {
            x: Math.floor(Math.random() * 1920),
            y: Math.floor(Math.random() * 1080),
          };
        }
      }

      // Inserimento variabili di default in base a trigger
      this.workFlowConfiguration.triggers.forEach((trigger) => {
        this.addDefaulVariables(trigger.type);
      });

      // Inserimento variabili di default in base ad azioni
      this.workFlowConfiguration.actions.forEach((action) => {
        this.addDefaultActionVariables(action.type);
      });
    });

    this.#destroyRef.onDestroy(() => {
      s0.unsubscribe();
    });
    let signalrSub = this.#siaSignalrService.serviceMessage$.subscribe(
      (msg) => {
        if (
          msg?.messageTypeId == 'workflow-execution-list' &&
          msg?.senderId == this.workFlowConfiguration.id
        ) {
          let message: {
            status: string;
            description: string;
            executionGuid: string;
          } = JSON.parse(msg.jsonMessage ?? '{}');

          if (message.status == 'pending') {
            // Add new execution to the beginning of the array
            this.workflowInExecution.set([
              {
                executionId: message.executionGuid,
                status: 'pending',
                description: message.description,
              },
              ...this.workflowInExecution(),
            ]);
          } else {
            // Update the status of the existing execution
            const executions = this.workflowInExecution().map((exec) => {
              if (exec.executionId === message.executionGuid) {
                return {
                  ...exec,
                  status: message.status,
                  // Keep the description as is
                };
              }
              return exec;
            });
            this.workflowInExecution.set(executions);
          }

          // order by status:pending first, complete then and last the executing
          this.workflowInExecution.set(
            this.workflowInExecution().sort((a, b) => {
              const statusOrder: { [key: string]: number } = {
                executing: 1,
                pending: 2,
                complete: 3,
                error: 4,
              };
              // 99 significa che stati non previsti vengono messi in fondo
              return (
                (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99)
              );
            }),
          );
        }

        if (
          msg?.messageTypeId == 'workflow' &&
          msg?.senderId == this.workFlowConfiguration.id
        ) {
          let message: {
            actionId: string;
            executionId: string;
            startExecutionDate?: Date;
            endExecutionDate?: Date;
          } = JSON.parse(msg.jsonMessage ?? '{}');
          message.actionId = message.actionId.toLowerCase();
          if (this.#executionId != message.executionId) {
            this.#executionId = message.executionId;
            this.actionsInExecution.set({});
            this.workFlowConfiguration.connections?.map(
              (c) => (c.executed = false),
            );
          }
          this.actionsInExecution.set({
            ...this.actionsInExecution(),
            [message.actionId]: !message.endExecutionDate
              ? 'executing'
              : 'completed',
          });
          let actionsParents = this.workFlowConfiguration.actions.filter(
            (a) =>
              a.children
                ?.map((child) => child.toLowerCase())
                ?.includes(message.actionId) &&
              this.actionsInExecution()[a.id.toLowerCase()] == 'completed',
          );
          this.workFlowConfiguration.connections?.forEach((connection) => {
            if (
              connection.to.toLowerCase().includes(message.actionId) &&
              actionsParents.findIndex((ap) =>
                connection.from.toLowerCase().includes(ap.id.toLowerCase()),
              ) != -1
            ) {
              connection.executed = true;
            }
          });
          this.workFlowConfiguration.connections?.sort((a, b) => {
            if (a.executed && !b.executed) return 1;
            if (!a.executed && b.executed) return -1;
            return 0;
          });
          this.#cdr.detectChanges();
        }
      },
    );
    this.#destroyRef.onDestroy(() => {
      signalrSub.unsubscribe();
    });
  }

  addDefaulVariables(type: string) {
    // Elimina le variabili di default esistenti per il tipo specificato
    this.variables.set(this.variables().filter((v) => !v.isDefault));

    // Aggiungi le nuove variabili di default per il tipo specificato
    if (this.startingVariables[type]) {
      let prev = this.variables();
      this.variables.set([]);
      prev.push(
        ...this.startingVariables[type].map((name: string) => ({
          id: name,
          parentId: type,
          description: name + ' - ' + type + ' (trigger)',
          isDefault: true,
        })),
      );
      this.variables.set(prev);
    }
  }
  addVariable(action: AIProcessActionDTO) {
    this.variables.update((param) =>
      param.filter((x) => x.parentId != action.id),
    );
    let prev = this.variables();
    this.variables.set([]);
    prev.push({
      id: action.output ?? '',
      parentId: action.id,
      description: action.output + ' - ' + (action.description ?? ''),
      isDefault: false,
    });
    this.variables.set(prev);
  }

  async updatePerspective() {
    this.#perspective!.jsonConfig = JSON.stringify(this.workFlowConfiguration);

    await this.#siaComponentConfigService.updateComponentPerspective(
      this.#perspective!,
    );
  }

  getParentConditionColor(connection: any, toConnectionId: string): boolean {
    let toActionId = toConnectionId.replace(/-.$/, '');
    let action = this.workFlowConfiguration.actions.find(
      (x) => x.id == toActionId,
    );
    let color = true;
    if (!action?.parentsCondition) {
      color = false;
    }
    connection.fromColor = color;
    return color;
  }

  getEnableConditionColor(connection: any, toConnectionId: string) {
    let toActionId = toConnectionId.replace(/-.$/, '');
    let action = this.workFlowConfiguration.actions.find(
      (x) => x.id == toActionId,
    );
    let color = 'var(--sia-bg-warning)';
    if (!action?.enableCondition) {
      color = 'var(--connection-color)';
    }
    connection.toColor = color;
    return color;
  }

  actionForMenu: any;

  async onConnectionClick(connection: any) {
    if (!this.isAdmin) {
      return;
    }
    var prompt = this.#matDialog.open(PromptDialogComponent, {
      width: '350px',
      data: {
        title: `Collegamento tra azioni`,
        text: 'Inserisci la descrizione del collegamento',
        style: 'font-size:15px',
        promptValue: connection.description,
      },
    });
    var res = await firstValueFrom(prompt.afterClosed());
    if (res) {
      res = res.trim();
      connection.description = res;
      this.#cdr.detectChanges();
      this.fCanvas?.redraw();
    }
  }
  setActionForMenu(action: any, $event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();
    this.actionForMenu = action;
  }

  openNodeMenu(event: MouseEvent, node: any) {
    event.preventDefault();
    this.contextMenuNode = node;
    this.matMenuTrigger.openMenu();
  }

  fitToScreen(): void {
    clearTimeout(this.#fitToScreenTimeout);
    this.#fitToScreenTimeout = setTimeout(() => {
      this.fCanvas?.fitToScreen(PointExtensions.initialize(50, 50), true);
    }, 200);
    this.#destroyRef.onDestroy(() => {
      clearTimeout(this.#fitToScreenTimeout);
    });
  }
  triggers: any[] = [];
  addTrigger() {
    if (!this.workFlowConfiguration.triggers) {
      this.workFlowConfiguration.triggers = [];
    }

    this.workFlowConfiguration.triggers.push({
      type: 'FolderWatcher',
      folderPath: '',
      includeSubfolders: true,
      fileTypes: [],
    });
    this.manageTriggerActions();
  }

  deleteTrigger(index: number) {
    this.workFlowConfiguration.triggers?.splice(index, 1);
  }

  async logout(trigger: any) {
    let res = await this.#googleAuthService.logout(
      'workflow-' + this.workFlowConfiguration.id,
      trigger.account,
    );
    if (res) trigger.account = '';
  }

  closeWorkflowPopup() {
    this.workflowInExecution.set([]);
  }

  addTimeInterval(trigger: any) {
    if (!trigger.configurationStartTime) {
      trigger.configurationStartTime = [];
    }
    trigger.configurationStartTime.push(new Date());
  }

  removeTimeInterval(trigger: any, index: number) {
    trigger.configurationStartTime.splice(index, 1);
  }
  updateIntervalValue(trigger: any, index: number, value: string) {
    trigger.configurationStartTime[index] = value;
  }
}
